import React, { useEffect, useState } from 'react'
import s from './index.module.scss'
import DetailModalWrapper from '../../../components/DetailModalWrapper'
import SimpleTable from '../../../components/UI/SimpleTable'
import {
	useCancelOrderMutation,
	useChangeExciseInvoiceNumberMutation,
	useGetOrderByIdQuery,
	useGetOrderTrucksQuery,
	useGetPreorderTimeQuery,
	useUpdateOrderTruckMutation,
} from '../../../store/orders/orders.api'
import Select from '../../../components/UI/Select'
import Checkbox from '../../../components/UI/Checkbox'
import Button from '../../../components/UI/Button'
import { IChangeOrderTruckRequestData, IOrderDetail, IPreorderTime } from '../../../models/models'
import { ReactComponent as DocIcon } from '../../../assets/icons/doc.svg'
import { exportHandler, formatPhone } from '../../../helpers'
import Table from '../../../components/Table'
import { orderAddressesColumns, orderInfoColumns } from '../../../constants/ordersPage'
import Input from '../../../components/UI/Input'
import { ReactComponent as EditIcon } from '../../../assets/icons/edit.svg'
import { ReactComponent as SaveIcon } from '../../../assets/icons/save.svg'
import dateFormat from 'dateformat'
import classNames from 'classnames'
import { useActions } from '../../../hooks/useActions'
import { useAppSelector } from '../../../hooks/useAppSelector'

interface IFormProps {
	id: string | number
	orderData?: IOrderDetail
	timeData?: IPreorderTime[]
}

const OrdersDetailForm = ({ id, orderData, timeData }: IFormProps) => {
	const fastOrder = orderData?.orderType === 'Термінова доставка'

	const [showAll, setShowAll] = useState<boolean>(false)
	const [days, setDays] = useState<any[]>([])
	const [time, setTime] = useState<any[]>([])
	const [selectedTruck, setSelectedTruck] = useState<number>(0)
	const [selectedDay, setSelectedDay] = useState(orderData?.preOrderDay || '')
	const [selectedTime, setSelectedTime] = useState(orderData?.timeRangeId || '')
	const [isError, setIsError] = useState<boolean>(false)
	const [isSaved, setIsSaved] = useState<boolean>(false)

	const { data: trucksData } = useGetOrderTrucksQuery({ id, showAll })
	const [editFunc, { error, isLoading: isEditing }] = useUpdateOrderTruckMutation()

	const editHandler = () => {
		if (!fastOrder && orderData?.needTimeRangeForTruckChanging && (selectedDay === '' || selectedTime === '')) {
			setIsError(true)
			return
		}
		setIsError(false)

		const editData: IChangeOrderTruckRequestData = {
			truckId: selectedTruck,
			...(!fastOrder &&
				selectedDay !== '' &&
				selectedTime !== '' && {
					timeRangeId: Number(selectedTime),
					preOrderDay: Number(selectedDay),
					timeZone: 'Europe/Kyiv',
				}),
		}
		editFunc({ id, data: editData })
			.unwrap()
			.then((res) => {
				if (res.status === 'success') {
					setIsSaved(true)
					setTimeout(() => {
						setIsSaved(false)
					}, 3000)
				}
			})
	}

	useEffect(() => {
		if (orderData) {
			setSelectedTruck(Number(orderData?.currentTruckId))
			setSelectedDay(orderData?.preOrderDay.toString())
			setSelectedTime(orderData?.timeRangeId.toString())
		}
		if (timeData) {
			setDays(
				Array.from(
					new Set(
						timeData
							.map((i) => ({
								title: i.name,
								value: i.preOrderDay.toString(),
							}))
							.map((i) => JSON.stringify(i)),
					),
				).map((i) => JSON.parse(i)),
			)

			setTime(
				timeData.map((i) => ({
					id: i.preOrderDay.toString(),
					title: `${i.beginAt} - ${i.endAt}`,
					value: i.id.toString(),
				})),
			)
		}
	}, [orderData, timeData])

	return (
		<>
			<div className={s.wrapper}>
				<div>
					<Select
						label={'Номер паливовоза'}
						options={[{ title: 'Оберіть паливовоз', value: '' }, ...(trucksData?.map((i) => ({ title: i.number, value: i.id })) || [])]}
						value={selectedTruck}
						onChange={(ev: React.ChangeEvent<HTMLSelectElement>) => setSelectedTruck(Number(ev.target.value))}
					/>
					<Checkbox label={'Показати всі'} checked={showAll} onChange={(ev: React.ChangeEvent<HTMLInputElement>) => setShowAll(ev.target.checked)} />
				</div>
				<div>
					<Select
						label={'Дата доставки'}
						options={[{ title: 'Оберіть день', value: '' }, ...days]}
						value={selectedDay}
						onChange={(ev: React.ChangeEvent<HTMLSelectElement>) => {
							setSelectedDay(ev.target.value)
							setSelectedTime('')
						}}
						disabled={fastOrder}
					/>
				</div>
				<div>
					<Select
						label={'Час доставки'}
						options={selectedDay === '' ? [{ title: 'Оберіть день', value: '' }] : [{ title: 'Оберіть час', value: '' }, ...time.filter((i) => i.id === selectedDay)]}
						value={selectedTime}
						onChange={(ev: React.ChangeEvent<HTMLSelectElement>) => setSelectedTime(ev.target.value)}
						disabled={fastOrder}
					/>
				</div>
			</div>

			{isError && <p className={s.error}>Оберіть дату та час</p>}

			{error && 'data' in error && <p className={s.error}>{error?.data?.message}</p>}

			<Button className={s.btn} variant="filled" disabled={isEditing} onClick={editHandler}>
				{isSaved ? 'Збережено' : 'Зберегти'}
			</Button>
		</>
	)
}

interface IChangeNumberProps {
	id: number
	number: number
}

const ChangeNumber = ({ id, number }: IChangeNumberProps) => {
	const [changeNumber, { error }] = useChangeExciseInvoiceNumberMutation()

	const [isEditing, setIsEditing] = useState<boolean>(false)
	const [newNumber, setNewNumber] = useState<string>(number.toString())

	const saveHandler = () => {
		changeNumber({ id, number: Number(newNumber) })
			.unwrap()
			.then(() => setIsEditing(false))
	}

	return (
		<div>
			<div className={s.changeNumberWrapper}>
				<span>№ накладної:</span>
				{isEditing ? (
					<>
						<Input value={newNumber} onChange={(ev: React.ChangeEvent<HTMLInputElement>) => setNewNumber(ev.target.value)} className={s.changeNumberInput} />
						<SaveIcon onClick={saveHandler} />
					</>
				) : (
					<>
						<b>{newNumber}</b>
						<EditIcon onClick={() => setIsEditing(true)} />
					</>
				)}
			</div>
			{error && 'data' in error && <p className={s.error}>{error.data.message}</p>}
		</div>
	)
}

interface IProps {
	id: string | number
	closeHandler: () => void
}

const OrdersDetailModal: React.FC<IProps> = ({ id, closeHandler }) => {
	const { permissions } = useAppSelector((state) => state.common)

	const { data, isLoading } = useGetOrderByIdQuery({ id })
	const { isLoading: trucksLoading } = useGetOrderTrucksQuery({
		id,
		showAll: false,
	})
	const { data: timeData, isLoading: timeLoading } = useGetPreorderTimeQuery({})
	const [calcelOrder] = useCancelOrderMutation()

	const { setLegalPersonId, setNaturalPersonId, setIsFop } = useActions()

	const [error, setError] = useState<string>('')
	const [showForm, setShowForm] = useState<boolean>(false)

	const detailInfo = [
		{
			title: 'Назва/ПІБ',
			value: (
				<span
					className={s.userLink}
					onClick={() => {
						if (data?.customer?.isLegalPerson) {
							setLegalPersonId(data.customer.entityId)

							if (data.customer.isFop) {
								setIsFop(true)
							}
						} else {
							setNaturalPersonId(data!.customer!.id)
						}
					}}
				>
					{data?.customer?.fullName || ''}
				</span>
			),
		},
		{
			title: 'Номер телефону',
			value: data?.customer?.phone ? (
				<a className={s.userLink} href={`tel:${data.customer.phone.replaceAll('+', '')}`}>
					{formatPhone(data.customer.phone) || '-'}
				</a>
			) : (
				''
			),
		},
		{ title: 'E-mail', value: data?.customer?.email || '' },
	]

	const orderInfo = [
		{ title: 'Тип', value: data?.orderType || '' },
		{
			title: 'Дата замовлення',
			value: dateFormat(data?.creationDate, 'dd.mm.yyyy') || '',
		},
		{
			title: 'Час замовлення',
			value: dateFormat(data?.creationDate, 'HH:MM') || '',
		},
	]

	const deliveryInfo = [
		{ title: 'Номер паливовоза', value: data?.currentTruckNumber || '' },
		{
			title: 'Дата доставки',
			value: dateFormat(data?.date, 'dd.mm.yyyy') || '',
		},
		{
			title: 'Час доставки',
			value: dateFormat(data?.date, 'HH:MM') + `${data?.toDate ? ` - ${dateFormat(data.toDate, 'HH:MM')}` : ''}` || '',
		},
	]

	const downloadPdf = async (url: string, title: string) => {
		try {
			await exportHandler(url, title, 'pdf', true)
		} catch (err) {
			if (err instanceof Error) {
				setError(err?.message)
			}
		}
	}

	const cancelHandler = () => {
		if (window.confirm('Ви впевнені, що бажаете скасувати замовлення?')) {
			setError('')
			calcelOrder({ id })
				.unwrap()
				.catch((err) => {
					setError(err.data.message)
				})
		}
	}

	return (
		<DetailModalWrapper isLoading={isLoading || trucksLoading || timeLoading} closeHandler={closeHandler}>
			<div className={s.titleWrapper}>
				<div className={s.titleWrapper}>
					{data?.canWatchDocuments && (
						<div className={s.docsWrapper}>
							<div className={s.docsIcon}>
								<DocIcon />
							</div>
							<div className={s.docsList}>
								<div onClick={() => downloadPdf(`orders/${id}/documents/sales-invoice?pdfMode=3`, 'Видаткова накладна')}>Видаткова накладна</div>
								<div onClick={() => downloadPdf(`orders/${id}/documents/excise-invoice?pdfMode=3`, 'Акцизна накладна')}>Акцизна накладна</div>
								<div onClick={() => downloadPdf(`orders/${id}/documents/ttn?pdfMode=3`, 'ТТН')}>ТТН</div>
							</div>
						</div>
					)}
					<div className={s.titleWrapper}>
						<h2 className={s.title}>Замовлення №{data?.orderId}</h2>
						{!!data?.code && <span className={s.code}>Код: {data?.code}</span>}
					</div>
				</div>
				{data?.canBeCancelled && (
					<span className={s.cancelBtn} onClick={cancelHandler}>
						Скасувати
					</span>
				)}
			</div>

			{error && <p className={s.error}>{error}</p>}

			<p className={s.info}>
				Статус: <span style={{ color: data?.status.color }}>{data?.status.text}</span>
			</p>
			<p className={s.info}>Коментар до замовлення: {data?.comment || '-'}</p>

			<DetailModalWrapper.Title>Інформація про замовника</DetailModalWrapper.Title>
			<SimpleTable data={detailInfo} isColumn />

			<div className={classNames(s.flex, s.flexMargin)}>
				<div>
					<DetailModalWrapper.Title>Інформація про замовлення</DetailModalWrapper.Title>
					<SimpleTable data={orderInfo} isColumn />
				</div>
				<div>
					<div className={s.flex}>
						<DetailModalWrapper.Title>Інформація про доставку</DetailModalWrapper.Title>
						{permissions.includes('ORDERS_UPDATE') && (
							<span className={s.editBtn} onClick={() => setShowForm((prev) => !prev)}>
								Змінити
							</span>
						)}
					</div>
					<SimpleTable data={deliveryInfo} isColumn />
				</div>
			</div>

			{showForm && <OrdersDetailForm id={id} orderData={data} timeData={timeData} />}

			<DetailModalWrapper.Title>Загальні суми</DetailModalWrapper.Title>
			<Table
				data={[
					{
						name: 'Замовлено',
						...data?.totalOrderedInfo,
					},
					{
						name: 'Факт',
						...data?.totalFilledInfo,
					},
				]}
				columns={[
					{
						accessorKey: 'name',
						header: '',
						enableSorting: false,
					},
					...orderInfoColumns,
				]}
				maxHeight={'205px'}
			/>

			{data?.filledByAddressesInfo &&
				data.filledByAddressesInfo?.length > 0 &&
				data.filledByAddressesInfo.map((i, index) => (
					<div key={index}>
						<div className={s.addressWrapper}>
							<div>
								<p className={s.addressNumber}>Адреса №{index + 1}</p>
								<a href={`https://www.google.com/maps/place/${i.point.latitude},${i.point.longitude}`} target="_blank" className={s.addressName}>
									{i.addressName}
								</a>
								<p className={s.addressTitle}>
									Коментар по адресі: <span className={s.addressComment}>{i.comment || '-'}</span>
								</p>
							</div>
							{i?.orderAddressId && i?.exciseInvoiceNumber && permissions.includes('ORDERS_UPDATE') && <ChangeNumber id={i.orderAddressId} number={i.exciseInvoiceNumber} />}
						</div>
						<Table data={i?.containers || []} columns={orderAddressesColumns} maxHeight={'205px'} />
						{i.totalProductPrices.map((j, jIndex) => (
							<p key={jIndex} className={s.addressPrice}>
								Загальна фактична сума за {j.productName} за адресою №{index + 1} <span>{(j.price / 100).toFixed(2)} грн</span>
							</p>
						))}
					</div>
				))}
		</DetailModalWrapper>
	)
}

export default OrdersDetailModal
